import React, { useState, useRef, useEffect } from "react";
import { CSS } from "@dnd-kit/utilities";
import { useSortable } from "@dnd-kit/sortable";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import ProfileAvatar from "../../components/globalFunctions/globalFunctions";

const Task = ({ task, depth, isParent, isCollapsed, subtaskCount, onToggle, handleDelete, task_status, onUpdate, statusOptions = [], onClickTaskDetail, onAddSubtask, userList }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: String(task.id) });
  const { id, title, parent, assignee } = task;

  const rowStyle = {
    transition,
    transform: CSS.Transform.toString(transform),
    backgroundColor: isDragging ? "#d7eefdff" : "",
    paddingLeft: `${(task.depth || 0) * 16}px`,
  };

  // Local edit state
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(task.title || "");
  const dropdownRef = useRef(null);
  const titleInputRef = useRef(null);

  const [dueDate, setDueDate] = useState(task.dueDate ? new Date(task.dueDate) : null);
  const [statusValue, setStatusValue] = useState(task.task_status || task_status);

  useEffect(() => {
    setTitleValue(task.title || "");
    setDueDate(task.dueDate ? (task.dueDate instanceof Date ? task.dueDate : new Date(task.dueDate)) : null);
    // setStatusValue(task.task_status || task_status);
    setStatusValue(task.task_status);
  }, [task]);

  useEffect(() => {
    if (editingTitle) {
      setTimeout(() => {
        titleInputRef.current?.focus();
      }, 0);
    }
  }, [editingTitle]);

  // Save helpers
  const saveTitle = () => {
    const newVal = titleValue?.toString() ?? "";
    if (newVal !== (task.title || "")) {
      onUpdate({ title: newVal });
    }
    setEditingTitle(false);
  };

  const statusIcons = {
    completed: <i className="fa fa-check-circle"></i>,
    "in progress": <i className="fa fa-adjust"></i>,
    todo: <i className="ri-donut-chart-fill"></i>,
  };
  const statusObj = statusOptions.find((s) => s.id === statusValue);

  const statusLabel = statusObj?.task_status?.toLowerCase();

  const statusIcon = statusIcons[statusLabel] || <i className="ri-donut-chart-fill"></i>;

  const saveStatus = (newStatusId) => {
    if (newStatusId !== statusValue) {
      setStatusValue(newStatusId);
      onUpdate({ task_status: newStatusId }, task.task_status);
    }
  };

  const saveDueDate = (dateVal) => {
    // convert to YYYY-MM-DD string if backend expects that; here we send ISO date string
    const dateToSend = dateVal ? (dateVal instanceof Date ? dateVal.toISOString() : dateVal) : null;
    const prev = task.dueDate ? (task.dueDate instanceof Date ? task.dueDate.toISOString() : String(task.dueDate)) : null;
    if ((dateToSend ?? null) !== (prev ?? null)) {
      onUpdate({ dueDate: dateToSend });
    }
    setDueDate(dateVal);
  };

  return (
    <tr ref={setNodeRef} style={rowStyle} className="hover-row">
      <td style={{ width: "60%" }}>
        <span className="task-drag-icon" {...attributes} {...listeners}>
          <i className="ri-draggable"></i>
        </span>

        <div className="form-check form-check-custom">
          <input type="checkbox" className="form-check-input" />
          <label className="form-check-label"></label>
        </div>

        {isParent ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onToggle(task.id);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            className="subtask-collapse-icon"
          >
            <i className={isCollapsed ? "ri-arrow-right-s-fill" : "ri-arrow-down-s-fill"} />
          </button>
        ) : (
          <span className="subtask-blank" />
        )}
        <div className={task.parent ? "task-detail subtask-detail" : "task-detail"}>
          <div>
            {/* ICON TRIGGER */}
            <span onClick={() => dropdownRef.current?.show()}>{statusIcon}</span>

            {/* PRIMEREACT DROPDOWN (hidden arrow) */}
            <Dropdown
              ref={dropdownRef}
              value={statusValue}
              onChange={(e) => {
                saveStatus(e.value); // now value is ID
              }}
              options={statusOptions.map((opt) => ({
                label: opt.task_status,
                value: opt.id,
                icon: statusIcons[opt.task_status.toLowerCase()] || <i className="ri-donut-chart-fill"></i>,
              }))}
              itemTemplate={(option) => (
                <div>
                  {option.icon} &nbsp;
                  <span>{option.label}</span>
                </div>
              )}
              valueTemplate={() => <div style={{ display: "none" }} />}
              panelClassName="status-picker-panel"
              className="status-picker"
              dropdownIcon={null}
              style={{ width: "1px", opacity: 0, pointerEvents: "none" }}
            />
          </div>

          {editingTitle ? (
            <input
              ref={titleInputRef}
              type="text"
              className="form-control task-title-input"
              value={titleValue}
              onChange={(e) => setTitleValue(e.target.value)}
              onBlur={saveTitle}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  saveTitle();
                }
                if (e.key === "Escape") {
                  setTitleValue(task.title || "");
                  setEditingTitle(false);
                }
              }}
            />
          ) : (
            <button type="button" className="btn btn-task-title" onClick={() => onClickTaskDetail(task, id)}>
              {title}
            </button>
          )}

          {isParent && !editingTitle ? (
            <span
              className="subtask-collapse-count"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onToggle(task.id);
              }}
            >
              <i className="ri-git-merge-line"></i>
              {subtaskCount}
            </span>
          ) : null}
        </div>
        <span className="subtask-add-btn">
          <button
            type="button"
            className="btn"
            title="Rename task"
            onClick={(e) => {
              e.stopPropagation();
              setEditingTitle(true);
            }}
          >
            <i className="ri-edit-2-line"></i>
          </button>

          {parent === 0 ? (
            <button type="button" className="btn" title="Add Subtask" onClick={() => onAddSubtask(task)}>
              <i className="ri-add-line"></i>
            </button>
          ) : null}
        </span>
      </td>
      <td>
        <div className="assignee-block">
          {assignee ? (
            <i className="profile-pic">
              <ProfileAvatar name={userList.find((u) => u.id === assignee)?.name || ""} />
            </i>
          ) : (
            <i className="ri-user-add-line"></i>
          )}
          <Dropdown
            value={task.assignee || null}
            options={userList.filter((u) => u.role !== 1)}
            optionLabel="name"
            optionValue="id"
            placeholder="Select Assignee"
            filter
            appendTo="self"
            collapseIcon="null"
            dropdownIcon="null"
            autoFocus="false"
            className="customised-dropdown"
            onChange={(e) => {
              const newAssignee = e.value;
              onUpdate({ assignee: newAssignee }); // uses your existing inline save
            }}
          />
        </div>
      </td>
      <td>
        <Calendar
          value={dueDate}
          onChange={(e) => {
            const val = e.value;
            setDueDate(val);
            // Save immediately when changed
            saveDueDate(val);
          }}
          onBlur={() => {
            // ensure saving if user used keyboard
            saveDueDate(dueDate);
          }}
          dateFormat="M dd yy"
          showIcon
          showButtonBar
          readOnlyInput={false}
        />
      </td>
      <td>
        <button type="button" className="task-more-link flag" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
          <i className="ri-flag-2-line"></i>
        </button>
        <div className="dropdown task-dropdown">
          <div className="dropdown-menu dropdown-menu-end">
            <a className="dropdown-item text-danger">
              <i className="ri-flag-2-fill"></i> High
            </a>
            <a className="dropdown-item text-warning">
              <i className="ri-flag-2-fill"></i> Medium
            </a>
            <a className="dropdown-item text-green">
              <i className="ri-flag-2-fill"></i> Low
            </a>
          </div>
        </div>
      </td>
      <td>
        <button type="button" className="task-more-link" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
          <i className="ri-more-fill"></i>
        </button>
        <div className="dropdown task-dropdown">
          <div className="dropdown-menu dropdown-menu-end">
            <a className="dropdown-item" href="#">
              <i className="ri-box-3-line"></i> Task type
            </a>
            <a className="dropdown-item" href="#">
              <i className="ri-file-copy-line"></i> Duplicate
            </a>
            <a className="dropdown-item" href="#">
              <i className="ri-logout-box-r-line"></i> Move
            </a>
            <a className="dropdown-item" href="#">
              <i className="ri-mail-send-line"></i> Send email
            </a>
            <a className="dropdown-item" href="#">
              <i className="ri-layout-5-line"></i> Templates
            </a>
            <div className="dropdown-divider my-1"></div>
            <a className="dropdown-item" href="#">
              <i className="ri-archive-line"></i> Archive
            </a>
            <button
              type="button"
              className="dropdown-item dropdown-item-delete"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (typeof handleDelete === "function") {
                  handleDelete(task.id);
                }
              }}
            >
              <i className="ri-delete-bin-line"></i> Delete
            </button>
          </div>
        </div>
      </td>
    </tr>
  );
};

export default Task;
